import React, { useEffect, useState, useRef } from 'react';
import { Button } from '@material-ui/core';
import "./LenderDashboard.css";
import { getAllLenderRequests, isDraftRequest } from "../shared/services/LenderRequestService";
import ReactSelect from 'react-select';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { AgGridReact } from 'ag-grid-react';
import { ModuleRegistry } from 'ag-grid-community';
import { SetFilterModule } from 'ag-grid-enterprise';
import RefreshIcon from "@material-ui/icons/Refresh";
import RequestDateRenderer from "../LenderRequests/List/AgGrid/RequestDateRenderer";
import EscalatedIndicatorRenderer from "../LenderRequests/List/AgGrid/EscalatedIndicatorRenderer";
import EditableCellRenderer from "../shared/components/AgGrid/EditableCellRenderer";
import { AssignReviewerModal } from "../LenderRequests/AssignReviewer/AssignReviewerModal";
import { fetchReviewers } from "../shared/services/LenderRequestService";
import { SubTypeTooltipRenderer } from "../shared/components/AgGrid/SubTypeTooltipRenderer";
import { REQUEST_STATUSES, CLOSED_STATUSES, IN_PROGRESS_STATUSES, OPEN_STATUSES } from "../shared/constants/RequestStatuses";
import { REQUEST_TYPES } from "../shared/constants/RequestTypes";
import { isProjectStandardRole, isAdminRole } from '../../redux/actions/userAction';
import { dateColumnComparator } from '../LenderRequests/List/AgGrid/dateUtils';
import SlaColorFilter from "../LenderRequests/List/AgGrid/SlaColorFilter";
import { AccessTime } from '@material-ui/icons';

// Register the SetFilterModule
ModuleRegistry.registerModules([SetFilterModule]);

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const hashStringToInt = (value) => {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }
  return hash;
};

const buildDistinctChartColor = (key, usedHues) => {
  const baseHash = hashStringToInt(key || 'unknown');
  let hue = baseHash % 360;
  let attempts = 0;

  while (usedHues.has(hue) && attempts < 360) {
    hue = (hue + 47) % 360;
    attempts += 1;
  }

  usedHues.add(hue);

  return {
    backgroundColor: `hsla(${hue}, 72%, 46%, 0.72)`,
    borderColor: `hsl(${hue}, 72%, 46%)`,
  };
};

const CHART_SEGMENT_PAGE_SIZE = 15;

const subtotalPlugin = {
  id: 'subtotalPlugin',
  afterDraw(chart) {
    const { ctx, scales, chartArea, data } = chart;
    if (!scales.y || !data.datasets) return;
    const yScale = scales.y;
    ctx.save();
    ctx.font = '11px sans-serif';
    ctx.fillStyle = '#333';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    yScale.ticks.forEach((tick, i) => {
      const total = data.datasets.reduce((sum, ds) => sum + (ds.data[i] || 0), 0);
      if (total > 0) {
        const y = yScale.getPixelForTick(i);
        ctx.fillText(String(total), chartArea.right + 8, y);
      }
    });
    ctx.restore();
  },
};

const multiSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '34px',
    borderColor: state.isFocused ? '#1176b5' : '#c7cdd6',
    boxShadow: state.isFocused ? '0 0 0 1px #1176b5' : 'none',
    '&:hover': {
      borderColor: '#1176b5',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '2px 8px',
  }),
  placeholder: (base) => ({
    ...base,
    color: '#6b7280',
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#e8f1fb',
    borderRadius: '6px',
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#0f5f95',
    fontWeight: 600,
    padding: '2px 6px',
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#0f5f95',
    '&:hover': {
      backgroundColor: '#cfe3f5',
      color: '#0b4e7a',
    },
  }),
  menu: (base) => ({
    ...base,
    zIndex: 20,
  }),
  menuList: (base) => ({
    ...base,
    fontSize: '14px',
  }),
  option: (base) => ({
    ...base,
    fontSize: '14px',
  }),
};

const mapSlaColorToHex = (value) => {
  if (!value || typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  if (normalized === 'RED') return '#dc3545';
  if (normalized === 'YELLOW' || normalized === 'AMBER') return '#ffc107';
  if (normalized === 'GREEN') return '#28a745';
  if (normalized === 'BLUE') return '#007bff';
  if (normalized === 'GREY' || normalized === 'GRAY') return '#bbbbbb';
  return null;
};

const LenderDashboard = () => {
  const [lastUpdated, setLastUpdated] = useState('Never');
  const [gridData, setGridData] = useState([]);
  const [masterGridData, setMasterGridData] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [activeCard, setActiveCard] = useState('allCases');
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState();
  const [reviewers, setReviewers] = useState([]);
  const [visiblePairCount, setVisiblePairCount] = useState(CHART_SEGMENT_PAGE_SIZE);
  const [inProgressSubFilter, setInProgressSubFilter] = useState(null);
  const [completedSubFilter, setCompletedSubFilter] = useState(null);
  const gridRef = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedCount, setSelectedCount] = useState(0);
  const [isBulkAssigning, setIsBulkAssigning] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkSuccessMessage, setBulkSuccessMessage] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const chartSourceData = useRef([]);
  const [filterState, setFilterState] = useState({ requestType: [], subType: [], analyst: [] });
  const [cardBaseData, setCardBaseData] = useState([]);
  const isUnassignedQueueItem = (item) => item.statusName === REQUEST_STATUSES.PENDING_REVIEW;

  const getPairLabel = (typeName, subTypeName) => {
    if (!typeName && !subTypeName) return 'Unknown';
    if (subTypeName) return `${typeName} / ${subTypeName}`;
    return typeName || 'Unknown';
  };

  const sizeColumnsToFit = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.sizeColumnsToFit();
    }
  };

  const getNativeSelectValues = (event) => {
    const selectedOptions = Array.from(event.target.selectedOptions || []);
    if (selectedOptions.length > 0) {
      return selectedOptions.map(option => option.value);
    }
    if (typeof event.target.value === 'string' && event.target.value.length > 0) {
      return [event.target.value];
    }
    return [];
  };

  const handleNativeFilterChange = (key, event) => {
    const values = getNativeSelectValues(event);
    setFilterState(previous => ({
      ...previous,
      [key]: values,
      ...(key === 'requestType' ? { subType: [] } : {}),
    }));
  };

  const fetchGridData = async () => {
    try {
      const data = await getAllLenderRequests();
      if (Array.isArray(data)) {
        const enriched = enrichWithDecision(data);
        // isDraftRequest checks externalStatus + statusName case-insensitively.
        // The PS gridRequestFeeds endpoint may return statusName/externalStatus as
        // 'Draft', '' or other values â€” the helper handles all cases.
        const nonDraft = enriched.filter(item => !isDraftRequest(item));
        setGridData(nonDraft);
        setMasterGridData(nonDraft);
        setCardBaseData(nonDraft);
        chartSourceData.current = nonDraft;
        setVisiblePairCount(CHART_SEGMENT_PAGE_SIZE);
        setInProgressSubFilter(null);
        setCompletedSubFilter(null);
        setChartData(generateChartDataFromGrid(nonDraft, CHART_SEGMENT_PAGE_SIZE));
        setLastUpdated(new Date().toLocaleString());
        setBulkError(null);
        setActiveCard('allCases');
        setFilterState({ requestType: [], subType: [], analyst: [] });
        // Clear AG Grid column filters and reset pagination on refresh
        if (gridRef.current?.api) {
          gridRef.current.api.setFilterModel(null);
          gridRef.current.api.paginationGoToFirstPage();
        }
      }
    } catch (error) {
      console.error('Error fetching grid data:', error);
      setBulkError('Unable to load dashboard data. Please refresh and try again.');
    }
  };

  // Helper function to apply card filtering logic
  const applyCardFilter = (data, cardType) => {
    if (cardType === 'allCases') {
      // All Cases = sum of unassigned + new + under review + in progress + escalated cases
      return data.filter(item => OPEN_STATUSES.includes(item.statusName));
    } else if (cardType === 'unassignedCases') {
      return data.filter(item => isUnassignedQueueItem(item));
    } else if (cardType === 'newCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.NEW);
    } else if (cardType === 'inProgressCases') {
      return data.filter(item => IN_PROGRESS_STATUSES.includes(item.statusName));
    } else if (cardType === 'underReviewCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.UNDER_REVIEW);
    } else if (cardType === 'completedCases') {
      return data.filter(item => CLOSED_STATUSES.includes(item.statusName) && (item.isCompletedToday || item.isCompletedYesterday));
    } else if (cardType === 'escalatedCases') {
      return data.filter(item => item.statusName === REQUEST_STATUSES.ESCALATED);
    } else {
      return data;
    }
  };

  // Targeted row update for assignments without affecting filters
  const updateAssignedRow = async (requestId, newAssignedTo, newAssignedToName) => {
    try {
      if (gridRef.current?.api) {
        // Find the row that needs updating
        const rowNode = gridRef.current.api.getRowNode(requestId);
        if (rowNode) {
          // Update the row data with new assignment info
          const updatedData = {
            ...rowNode.data,
            assignedToLdapId: newAssignedTo,
            assignedToName: newAssignedToName
          };
          
          // Use transaction API to update just this row
          gridRef.current.api.applyTransaction({ update: [updatedData] });
        } else {
          // If row not found, do a minimal refresh for this specific case
          const data = await getAllLenderRequests();
          if (Array.isArray(data)) {
            const enriched = enrichWithDecision(data);
            const updatedRow = enriched.find(item => item.requestId === requestId);
            if (updatedRow) {
              gridRef.current.api.applyTransaction({ update: [updatedRow] });
            }
          }
        }
      }
    } catch (error) {
      console.error('Error updating assigned row:', error);
      setBulkError('Unable to update assigned row. Please refresh and try again.');
    }
  };

  useEffect(() => {
    fetchGridData();
    
    // Fetch reviewers for assignment modal
    const loadReviewers = async () => {
      try {
        const reviewerList = await fetchReviewers();
        setReviewers(reviewerList);
      } catch (error) {
        console.error('Error fetching reviewers:', error);
        setBulkError('Unable to load reviewers. Please refresh and try again.');
      }
    };
    loadReviewers();
  }, []);

  const onSelectionChanged = () => {
    const selected = gridRef.current?.api?.getSelectedRows() ?? [];
    setSelectedRows(selected);
    setSelectedCount(selected.length);
  };

  const handleBulkAssignComplete = (response, assignedToLdapId, assignedToName) => {
    setIsBulkAssigning(false);
    setShowBulkModal(false);
    selectedRows.forEach(row => {
      updateAssignedRow(row.requestId, assignedToLdapId, assignedToName);
    });
    gridRef.current?.api?.deselectAll();
    setSelectedRows([]);
    setSelectedCount(0);
    setBulkSuccessMessage(`Successfully assigned ${selectedCount} request(s).`);
    setBulkError(null);
  };

  const handleBulkAssignError = (error) => {
    setIsBulkAssigning(false);
    setBulkError(error?.message ?? 'Bulk assignment failed. Please try again.');
    setBulkSuccessMessage(null);
  };

  // Direct assignment handler for LenderDashboard that preserves all filters
  const handleAssignment = async (selectedReviewer, notes, escalate, notesExternal) => {
    if (!selectedRequest || (!selectedReviewer && !escalate)) return false;

    try {
      const passData = JSON.parse(sessionStorage.getItem("passData"));
      const request = {
        requestStatusId: selectedRequest.projectRequestStatusId,
        requestMasterTrackerId: selectedRequest.requestId,
        assignTo: selectedReviewer ? selectedReviewer.userLdapId : null,
        notes: notes,
        isEscalated: escalate,
        notesIsExternal: notesExternal,
        createdBy: passData.user,
        updatedBy: passData.user,
      };

      const { assignReviewerToRequest } = await import("../shared/services/LenderRequestService");
      const status = await assignReviewerToRequest(request);

      if (status && gridRef.current?.api) {
        // Update only the specific row without any refresh
        const rowNode = gridRef.current.api.getRowNode(selectedRequest.requestId);
        if (rowNode) {
          const updatedData = {
            ...rowNode.data,
            assignedToLdapId: selectedReviewer ? selectedReviewer.userLdapId : null,
            assignedToName: selectedReviewer ? selectedReviewer.userName : null,
            ...(escalate ? { statusName: REQUEST_STATUSES.ESCALATED } : {})
          };
          gridRef.current.api.applyTransaction({ update: [updatedData] });
        }
      }

      return status;
    } catch (error) {
      console.error('Assignment error:', error);
      setBulkError(error?.message || 'Unable to assign reviewer. Please try again.');
      setBulkSuccessMessage(null);
      return false;
    }
  };

  const COMPLETED_STATUSES = CLOSED_STATUSES;

  // Helper function to check if a request was completed today based on item.statusName and item.latestStatusChangeDate
  const isCompletedToday = (item) => {
    if (!COMPLETED_STATUSES.includes(item.statusName)) return false;

    const dateToCheck = item.latestStatusChangeDate;
    if (!dateToCheck) return false;

    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
    const statusDate = new Date(dateToCheck);

    return statusDate >= startOfToday && statusDate <= endOfToday;
  };

  const isCompletedYesterday = (item) => {
    if (!COMPLETED_STATUSES.includes(item.statusName)) return false;
    const dateToCheck = item.latestStatusChangeDate;
    if (!dateToCheck) return false;
    const today = new Date();
    const yesterdayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
    const yesterdayEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const statusDate = new Date(dateToCheck);
    return statusDate >= yesterdayStart && statusDate < yesterdayEnd;
  };

  const enrichWithDecision = (data) => data.map(item => ({
    ...item,
    requestStatusDate: item.latestStatusChangeDate || item.requestStatusDate || null,
    slaColor: item.slaColor || null,
    hasDecision: COMPLETED_STATUSES.includes(item.statusName),
    isCompletedToday: isCompletedToday(item),
    isCompletedYesterday: isCompletedYesterday(item),
  }));

  // Extract ALL sub-type labels from an item (supports multi-sub-type requests)
  const getItemSubTypes = (item) => {
    const reasonCodes = item.reasonCodes;
    if (reasonCodes && reasonCodes.length > 0) {
      const codes = reasonCodes.map(rc => rc.reasonCode).filter(Boolean);
      if (codes.length > 0) return codes;
    }
    const docs = item.requestFeedDocumentDTOList;
    if (docs && docs.length > 0) {
      const uniqueSubTypes = [...new Set(docs.map(d => d.requestSubTypeName).filter(Boolean))];
      if (uniqueSubTypes.length > 0) return uniqueSubTypes;
    }
    return item.requestSubTypeName ? [item.requestSubTypeName] : [null];
  };

  const getItemPairLabels = (item) => {
    const typeName = item.requestTypeName || null;
    return getItemSubTypes(item).map(subTypeName => getPairLabel(typeName, subTypeName));
  };

  const getChartPairOrder = (data) => {
    const pairOrder = [];
    const seen = new Set();

    data.forEach(item => {
      getItemPairLabels(item).forEach(label => {
        if (!seen.has(label)) {
          seen.add(label);
          pairOrder.push(label);
        }
      });
    });

    return pairOrder.sort((a, b) => a.localeCompare(b));
  };

  // Generate chart data from grid data, segmented by requestTypeName/requestSubTypeName.
  // A request with multiple sub-types is counted once in each matching segment.
  // visibleCountOverride: cumulative number of type/sub-type pairs to show from the start.
  const generateChartDataFromGrid = (data, visibleCountOverride = CHART_SEGMENT_PAGE_SIZE) => {
    const allPairs = getChartPairOrder(data);
    const visiblePairs = allPairs.slice(0, visibleCountOverride);
    const hasMore = allPairs.length > visibleCountOverride;

    // Build display label map: subTypeName when present, else typeName
    const pairDisplayLabelMap = {};
    data.forEach(item => {
      const typeName = item.requestTypeName || 'Unknown';
      getItemSubTypes(item).forEach(subTypeName => {
        const key = getPairLabel(typeName, subTypeName);
        pairDisplayLabelMap[key] = subTypeName || typeName || 'Unknown';
      });
    });

    const pairLabels = visiblePairs.length > 0 ? visiblePairs : ['Unknown'];
    const currentPageSet = new Set(visiblePairs);

    const userMap = {};
    data.forEach(item => {
      const userName = item.assignedToName || 'Unassigned';
      const effectiveLabels = [...new Set(getItemPairLabels(item))].filter(label => currentPageSet.has(label));

      if (!userMap[userName]) {
        userMap[userName] = {};
        pairLabels.forEach(pair => {
          userMap[userName][pair] = 0;
        });
      }

      effectiveLabels.forEach(label => {
        userMap[userName][label] = (userMap[userName][label] || 0) + 1;
      });
    });

    const labels = Object.keys(userMap)
      .filter(user => Object.values(userMap[user]).some(count => count > 0))
      .sort((a, b) => {
        if (a === 'Unassigned') return 1;
        if (b === 'Unassigned') return -1;
        return a.localeCompare(b);
      });

    const usedHues = new Set();
    const datasets = pairLabels.map((pair) => {
      const chartColors = buildDistinctChartColor(pair, usedHues);
      return {
        label: pairDisplayLabelMap[pair] || pair,
        _pairKey: pair,
        data: labels.map(user => (userMap[user] ? userMap[user][pair] || 0 : 0)),
        backgroundColor: chartColors.backgroundColor,
        borderColor: chartColors.borderColor,
        borderWidth: 1,
        borderRadius: 5,
        barPercentage: 0.8,
        categoryPercentage: 0.8,
      };
    });

    return {
      labels,
      datasets,
      pagePairs: visiblePairs,
      hasMore,
    };
  };

  const handleCardClick = async (type) => {
    setActiveCard(type);
    setInProgressSubFilter(null);
    setCompletedSubFilter(null);
    if (type !== 'unassignedCases' && gridRef.current?.api) {
      gridRef.current.api.deselectAll();
    }
    // Re-fetch data from API on every card click
    try {
      const freshData = await getAllLenderRequests();
      if (Array.isArray(freshData)) {
        const enriched = enrichWithDecision(freshData);
        const nonDraft = enriched.filter(item => !isDraftRequest(item));
        setMasterGridData(nonDraft);

        // Use the helper function for consistent filtering
        const filteredData = applyCardFilter(nonDraft, type);

        setCardBaseData(filteredData);
        setFilterState({ requestType: [], subType: [], analyst: [] });
        setGridData(filteredData);
        // Chart reflects the card-filtered data
        chartSourceData.current = filteredData;
        setVisiblePairCount(CHART_SEGMENT_PAGE_SIZE);
        setChartData(generateChartDataFromGrid(filteredData, CHART_SEGMENT_PAGE_SIZE));
        setLastUpdated(new Date().toLocaleString());
        setBulkError(null);
        // Clear AG Grid column filters and reset pagination on card click
        if (gridRef.current?.api) {
          gridRef.current.api.setFilterModel(null);
          gridRef.current.api.paginationGoToFirstPage();
        }
      }
    } catch (error) {
      console.error('Error refreshing data:', error);
      setBulkError('Unable to refresh dashboard data. Please try again.');
    }
  };

  const handleInProgressSubFilter = (tier) => {
    const newFilter = tier === inProgressSubFilter ? null : tier;
    setInProgressSubFilter(newFilter);
    setActiveCard('inProgressCases');
    const inProgressBase = masterGridData.filter(item => IN_PROGRESS_STATUSES.includes(item.statusName));
    setCardBaseData(inProgressBase);
    setFilterState({ requestType: [], subType: [], analyst: [] });
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  const handleCompletedSubFilter = (tier) => {
    const newFilter = tier === completedSubFilter ? null : tier;
    setCompletedSubFilter(newFilter);
    setActiveCard('completedCases');
    const completedBase = masterGridData.filter(item => CLOSED_STATUSES.includes(item.statusName) && (item.isCompletedToday || item.isCompletedYesterday));
    setCardBaseData(completedBase);
    setFilterState({ requestType: [], subType: [], analyst: [] });
    if (gridRef.current?.api) {
      gridRef.current.api.setFilterModel(null);
    }
  };

  const handleChartClick = (event, elements, chart) => {
  if (!chart || !chart.scales || !chart.scales.y) return;

  if (elements?.length > 0 && chartData) {
    const { labels, datasets } = chartData;
    if (labels && datasets) {
      const clickedElement = elements[0];
      const clickedBarIndex = clickedElement.index;
      const clickedDatasetIndex = clickedElement.datasetIndex;
      const clickedAnalyst = labels[clickedBarIndex];
      const clickedRequestType = datasets[clickedDatasetIndex]?._pairKey ?? datasets[clickedDatasetIndex]?.label;

      const filteredData = chartSourceData.current.filter(item => {
        const userName = item.assignedToName || 'Unassigned';
        if (userName !== clickedAnalyst) return false;
        const itemLabels = getItemPairLabels(item);
        return itemLabels.some(label => label === clickedRequestType);
      });

      setGridData(filteredData);
    }
  } else if (!elements?.length && chartData) {
    const yScale = chart.scales.y;
    const clickY = event.native ? event.native.offsetY : event.offsetY;
    const tickCount = yScale.ticks.length;
    const tickHeight = tickCount > 0 ? (yScale.bottom - yScale.top) / tickCount : 0;
    for (let i = 0; i < tickCount; i++) {
      const tickPixel = yScale.getPixelForTick(i);
      if (Math.abs(clickY - tickPixel) <= tickHeight / 2) {
        const analystName = chartData.labels[i];
        const filtered = chartSourceData.current.filter(item =>
          (item.assignedToName || 'Unassigned') === analystName
        );
        setGridData(filtered);
        break;
      }
    }
  }
};

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    layout: {
      padding: { right: 50 },
    },
    plugins: {
      legend: { position: 'top', align: 'start' },
      title: { display: true, text: 'CPM Assigned Requests by Analyst and Request Type/Sub-Type' },
    },
    scales: {
      x: {
        stacked: true,
        title: { display: true, text: 'Number of Assigned Requests' },
        ticks: {
          font: { size: 12 },
          stepSize: 1,
          callback: function(value) {
            if (Number.isInteger(value)) return value;
            return null;
          }
        },
      },
      y: {
        stacked: true,
        position: 'left',
        title: { display: true, text: 'Users' },
        ticks: {
          font: { size: 11 },
          autoSkip: false,
          padding: 8,
          callback: function(value) {
            const label = this.getLabelForValue(value);
            return label.length > 20 ? label.substring(0, 18) + '...' : label;
          }
        },
      },
    },
    data: chartData || { labels: [], datasets: [] },
  };
  const chartDynamicHeight = Math.max(340, (chartData?.labels?.length || 0) * 42 + 120);

  const handleMoreClick = (event) => {
    event.preventDefault();
    const newCount = visiblePairCount + CHART_SEGMENT_PAGE_SIZE;
    setVisiblePairCount(newCount);
    setChartData(generateChartDataFromGrid(chartSourceData.current, newCount));
  };

  useEffect(() => {
    if (!cardBaseData.length) return;
    let filtered = cardBaseData;
    if (filterState.requestType.length > 0) {
      filtered = filtered.filter(item =>
        filterState.requestType.includes(item.requestTypeName || 'Unknown')
      );
    }
    if (filterState.subType.length > 0) {
      filtered = filtered.filter(item =>
        getItemSubTypes(item).some(subType => filterState.subType.includes(subType))
      );
    }
    if (filterState.analyst.length > 0) {
      filtered = filtered.filter(item =>
        filterState.analyst.includes(item.assignedToName || 'Unassigned')
      );
    }
    // Apply in-progress secondary SLA-tier sub-filter
    if (inProgressSubFilter === 'internal') {
      filtered = filtered.filter(r => (r.slaColor || '').trim().toUpperCase() !== 'BLUE');
    } else if (inProgressSubFilter === 'external') {
      filtered = filtered.filter(r => (r.slaColor || '').trim().toUpperCase() === 'BLUE');
    }
    // Apply completed sub-filter (yesterday / today)
    if (completedSubFilter === 'yesterday') {
      filtered = filtered.filter(r => r.isCompletedYesterday);
    } else if (completedSubFilter === 'today') {
      filtered = filtered.filter(r => r.isCompletedToday);
    }
    chartSourceData.current = filtered;
    setVisiblePairCount(CHART_SEGMENT_PAGE_SIZE);
    setGridData(filtered);
    setChartData(generateChartDataFromGrid(filtered, CHART_SEGMENT_PAGE_SIZE));
    if (gridRef.current?.api) {
      gridRef.current.api.paginationGoToFirstPage();
    }
  }, [filterState, cardBaseData, inProgressSubFilter, completedSubFilter]);

  
  const requestTypeOptions = [...new Set((cardBaseData.length ? cardBaseData : masterGridData).map(item => item.requestTypeName || 'Unknown'))]
    .sort()
    .map(value => ({ value, label: value }));
  const subTypeOptions = [...new Set(
    (cardBaseData.length ? cardBaseData : masterGridData)
      .filter(item => filterState.requestType.length === 0 || filterState.requestType.includes(item.requestTypeName || 'Unknown'))
      .flatMap(item => getItemSubTypes(item))
      .filter(Boolean)
  )].sort().map(value => ({ value, label: value }));
  const analystOptions = [...new Set((cardBaseData.length ? cardBaseData : masterGridData).map(item => item.assignedToName || 'Unassigned'))]
    .sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    })
    .map(value => ({ value, label: value }));

  const showBulkUI = isProjectStandardRole() || isAdminRole();
  const maxSelectionExceeded = selectedCount > 50;
  const assignButtonDisabled = selectedCount === 0 || maxSelectionExceeded || isBulkAssigning;

  const underReviewCount = masterGridData.filter(item => item.statusName === REQUEST_STATUSES.NEW).length;

  return (
    <div className="dashboard-container" data-testid="lender-dashboard">{/* Test ID for background refresh detection */}
        <div className="dashboard-header">
            <div className="refresh-section">
                <span style={{ color: 'var(--theme-blue)' }}>Last updated: {lastUpdated}</span>
                <RefreshIcon className="refresh-icon" onClick={fetchGridData} />
            </div>
        </div>
        {masterGridData.length > 0 ? (
            <>
                <div className="dashboard-cards-layout">
                    {(() => {
                        const allCases = masterGridData.filter(item => OPEN_STATUSES.includes(item.statusName)).length;
                      const unassignedCases = masterGridData.filter(item => isUnassignedQueueItem(item)).length;
                        const inProgressItems = masterGridData.filter(item => IN_PROGRESS_STATUSES.includes(item.statusName));
                        const internalCount = inProgressItems.filter(r => (r.slaColor || '').trim().toUpperCase() !== 'BLUE').length;
                        const externalCount = inProgressItems.filter(r => (r.slaColor || '').trim().toUpperCase() === 'BLUE').length;
                        const todayCount = masterGridData.filter(i => i.isCompletedToday).length;
                        const yesterdayCount = masterGridData.filter(i => i.isCompletedYesterday).length;
                        // Count escalated cases
                        const escalatedCases = masterGridData.filter(item => item.statusName === REQUEST_STATUSES.ESCALATED).length;
                        return (
                            <>
                                <div className={`card all-cases${activeCard === 'allCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('allCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('allCases'); }}}>
                                    <div style={{ width: '100%' }}>
                                        <h3>All Active Cases</h3>
                                        <p>{allCases}</p>
                                    </div>
                                </div>
                                <div className={`card unassigned-cases${activeCard === 'unassignedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('unassignedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('unassignedCases'); }}}>
                                    <div style={{ width: '100%' }}>
                                        <h3>Unassigned Cases</h3>
                                        <p>{unassignedCases}</p>
                                    </div>
                                </div>
                                <div className={`card new-cases${activeCard === 'newCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('newCases')} role="button" tabIndex={0} aria-label={`New Cases: ${underReviewCount}`} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('newCases'); }}}>
                                    <div style={{ width: '100%' }}>
                                        <h3>New Cases</h3>
                                        <p>{underReviewCount}</p>
                                    </div>
                                </div>
                                <div className={`card in-progress-cases${activeCard === 'inProgressCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('inProgressCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('inProgressCases'); }}}>
                                    <div style={{ width: '100%' }}>
                                        <h3>In Progress Cases</h3>
                                        <div className="in-progress-sub-cards">
                                            <div
                                                className={`sub-card sub-card-internal${inProgressSubFilter === 'internal' ? ' active' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); handleInProgressSubFilter('internal'); }}
                                            >
                                                <span>Internal</span>
                                                <strong>{internalCount}</strong>
                                            </div>
                                            <div
                                                className={`sub-card sub-card-external${inProgressSubFilter === 'external' ? ' active' : ''}`}
                                                onClick={(e) => { e.stopPropagation(); handleInProgressSubFilter('external'); }}
                                            >
                                                <span>External</span>
                                                <strong>{externalCount}</strong>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={`card completed-cases${activeCard === 'completedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('completedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('completedCases'); }}}>
                                  <div style={{ width: '100%' }}>
                                    <h3>Completed Cases</h3>
                                    <div className="in-progress-sub-cards">
                                      <div
                                        className={`sub-card sub-card-yesterday${completedSubFilter === 'yesterday' ? ' active' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); handleCompletedSubFilter('yesterday'); }}
                                      >
                                        <span>Yesterday</span>
                                        <strong>{yesterdayCount}</strong>
                                      </div>
                                      <div
                                        className={`sub-card sub-card-today${completedSubFilter === 'today' ? ' active' : ''}`}
                                        onClick={(e) => { e.stopPropagation(); handleCompletedSubFilter('today'); }}
                                      >
                                        <span>Today</span>
                                        <strong>{todayCount}</strong>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                                <div className={`card escalated-cases${activeCard === 'escalatedCases' ? ' card-active' : ''}`} onClick={() => handleCardClick('escalatedCases')} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick('escalatedCases'); }}}>
                                    <div style={{ width: '100%' }}>
                                        <h3>Escalated Cases</h3>
                                        <p>{escalatedCases}</p>
                                    </div>
                                </div>
                            </>
                        );
                    })()}
                </div>

                <div className="dashboard-filters">
                    <div className="dashboard-filter-group">
                        <label htmlFor="request-type-filter">Request Types</label>
                        <select
                          aria-hidden="true"
                          className="dashboard-legacy-select"
                          multiple
                          tabIndex={-1}
                          value={Array.isArray(filterState.requestType) ? filterState.requestType : []}
                          onChange={(event) => handleNativeFilterChange('requestType', event)}
                        >
                          {requestTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ReactSelect
                          inputId="request-type-filter"
                          classNamePrefix="dashboard-select"
                          isMulti
                          isSearchable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          placeholder="All Request Types"
                          options={requestTypeOptions}
                          value={requestTypeOptions.filter(option => filterState.requestType.includes(option.value))}
                          onChange={(selectedOptions) => setFilterState(s => ({
                            ...s,
                            requestType: (selectedOptions || []).map(option => option.value),
                            subType: [],
                          }))}
                          styles={multiSelectStyles}
                        />
                    </div>
                    <div className="dashboard-filter-group">
                        <label htmlFor="sub-type-filter">Sub-Types</label>
                        <select
                          aria-hidden="true"
                          className="dashboard-legacy-select"
                          multiple
                          tabIndex={-1}
                          value={Array.isArray(filterState.subType) ? filterState.subType : []}
                          onChange={(event) => handleNativeFilterChange('subType', event)}
                        >
                          {subTypeOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ReactSelect
                          inputId="sub-type-filter"
                          classNamePrefix="dashboard-select"
                          isMulti
                          isSearchable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          placeholder="All Sub-Types"
                          options={subTypeOptions}
                          value={subTypeOptions.filter(option => filterState.subType.includes(option.value))}
                          onChange={(selectedOptions) => setFilterState(s => ({
                            ...s,
                            subType: (selectedOptions || []).map(option => option.value),
                          }))}
                          styles={multiSelectStyles}
                        />
                    </div>
                    <div className="dashboard-filter-group">
                        <label htmlFor="analyst-filter">Analysts</label>
                        <select
                          aria-hidden="true"
                          className="dashboard-legacy-select"
                          multiple
                          tabIndex={-1}
                          value={Array.isArray(filterState.analyst) ? filterState.analyst : []}
                          onChange={(event) => handleNativeFilterChange('analyst', event)}
                        >
                          {analystOptions.map(option => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                        <ReactSelect
                          inputId="analyst-filter"
                          classNamePrefix="dashboard-select"
                          isMulti
                          isSearchable
                          closeMenuOnSelect={false}
                          hideSelectedOptions={false}
                          placeholder="All Analysts"
                          options={analystOptions}
                          value={analystOptions.filter(option => filterState.analyst.includes(option.value))}
                          onChange={(selectedOptions) => setFilterState(s => ({
                            ...s,
                            analyst: (selectedOptions || []).map(option => option.value),
                          }))}
                          styles={multiSelectStyles}
                        />
                    </div>
                    <button
                        className="dashboard-reset-button"
                        onClick={() => setFilterState({ requestType: [], subType: [], analyst: [] })}
                        type="button"
                    >
                        Reset
                    </button>
                </div>

                <div className="row dashboard-data-layout">
                  <div className="chart-container dashboard-chart-panel">
                        {chartData ? (
                            <>
                              <div className="dashboard-chart-scroll" style={{ height: `${chartDynamicHeight}px` }}>
                                <div className="dashboard-chart-pagination dashboard-chart-pagination-inline">
                                  {chartData?.hasMore && (
                                    <a href="#" onClick={handleMoreClick} className="dashboard-chart-page-link">More</a>
                                  )}
                                </div>
                                <Bar
                                    key={JSON.stringify(chartData)}
                                    data={chartData}
                                    options={{
                                      ...chartOptions,
                                      onClick: (event, elements, chart) => handleChartClick(event, elements, chart),
                                    }}
                                    plugins={[subtotalPlugin]}
                                />
                              </div>
                            </>
                        ) : (
                            <p>Loading chart data...</p>
                        )}
                    </div>
                </div>

                <div className="dashboard-table-layout">
                      <div className="dashboard-grid dashboard-grid-panel">
                        {showBulkUI && (
                          <div className="bulk-assign-toolbar" style={{ padding: '8px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {bulkSuccessMessage && (
                              <div className="alert alert-success" role="alert" style={{ margin: 0, padding: '4px 12px', fontSize: '0.9rem' }}>
                                {bulkSuccessMessage}
                              </div>
                            )}
                            {bulkError && (
                              <div className="alert alert-danger" role="alert" style={{ margin: 0, padding: '4px 12px', fontSize: '0.9rem' }}>
                                {bulkError}
                              </div>
                            )}
                            {maxSelectionExceeded && (
                              <span className="bulk-assign-warning" style={{ color: '#b00', fontSize: '0.9rem' }}>
                                Maximum 50 requests allowed. Please deselect some rows.
                              </span>
                            )}
                            {selectedCount > 0 && (
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => { setIsBulkAssigning(true); setShowBulkModal(true); setBulkSuccessMessage(null); setBulkError(null); }}
                                disabled={assignButtonDisabled}
                                style={{ backgroundColor: assignButtonDisabled ? undefined : '#1176b5', color: '#fff', textTransform: 'none', marginLeft: '20px' }}
                              >
                                {isBulkAssigning ? 'Assigningâ€¦' : `Assign Selected (${selectedCount})`}
                              </Button>
                            )}
                          </div>
                        )}
                        <AgGridReact
                            ref={gridRef}
                          reactiveCustomComponents={false}
                            onGridReady={sizeColumnsToFit}
                            onGridSizeChanged={sizeColumnsToFit}
                            rowData={gridData}
                            getRowId={(params) => params.data.requestId}
                            columnDefs={[
                                { headerName: 'Project ID', field: 'projectId', minWidth: 100, maxWidth: 115, suppressMenu: false, floatingFilter: false, menuTabs: ['filterMenuTab'], filter: 'agTextColumnFilter', filterParams: { buttons: ['reset'] } },
                                { 
                                  headerName: 'Type', 
                                  field: 'requestTypeName',
                                  valueFormatter: (params) => {
                                    if (!params.value) return '';
                                    return params.value
                                      .replace(/\bStatus\b/gi, '')
                                      .replace(/\bLender\b/gi, '')
                                      .replace(/\s{2,}/g, ' ')
                                      .trim();
                                  },
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value,
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  menuTabs: ['filterMenuTab'],
                                  suppressMenu: false,
                                  floatingFilter: false
                                },
                                { 
                                  headerName: 'Sub Types', field: 'requestSubTypeName', minWidth: 280,
                                  filter: 'agTextColumnFilter',
                                  filterParams: {
                                    buttons: ['reset']
                                  },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  cellRenderer: SubTypeTooltipRenderer,
                                  valueGetter: (params) => {
                                    const reasonCodes = params.data?.reasonCodes;
                                    if (reasonCodes && reasonCodes.length > 0) {
                                      return reasonCodes.map(rc => rc.reasonCode).filter(Boolean).join(', ');
                                    }

                                    const docs = params.data?.requestFeedDocumentDTOList;
                                    if (docs && docs.length > 0) {
                                      const uniqueSubTypes = [...new Set(docs.map(d => d.requestSubTypeName).filter(Boolean))];
                                      if (uniqueSubTypes.length > 0) return uniqueSubTypes.join(', ');
                                    }
                                    
                                    const requestTypeName = params.data?.requestTypeName;
                                    if (requestTypeName === REQUEST_TYPES.REMOVE_DELIVERY_RESTRICTION) {
                                      const removeDrDetails = params.data?.removeDrDetails;
                                      return removeDrDetails?.map(dr => dr.restrcnShortName).join(', ') || '';
                                    }

                                    return params.data?.requestSubTypeName || '';
                                  }
                                },
                                {
                                  headerName: '',
                                  field: 'statusName',
                                  headerTooltip: 'Escalation Indicator',
                                  maxWidth: 40,
                                  cellRenderer: EscalatedIndicatorRenderer,
                                  sortable: false,
                                  filter: false,
                                  resizable: false,
                                  suppressMenu: true,
                                  floatingFilter: false
                                },
                                {
                                  headerName: 'Req. Status',
                                  field: 'internalStatus',
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value,
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  menuTabs: ['filterMenuTab'],
                                  suppressMenu: false,
                                  floatingFilter: false
                                },
                                { 
                                  headerName: 'Assigned To', 
                                  field: 'assignedToName', 
                                  minWidth: 180,
                                  filter: 'agSetColumnFilter',
                                  filterParams: {
                                    suppressSelectAll: false,
                                    suppressMiniFilter: false,
                                    suppressSorting: true,
                                    textFormatter: (value) => value || 'Unassigned',
                                    buttons: ['reset'],
                                    closeOnApply: true,
                                    suppressRemoveEntries: true
                                  },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  cellRenderer: EditableCellRenderer,
                                  cellRendererParams: {
                                    onDataRefresh: () => {
                                      // NO-OP: Prevent any refresh during assignments
                                      // LenderDashboard handles updates via direct assignment handler
                                    },
                                    openModal: (request) => {
                                      setSelectedRequest(request);
                                      setShowAssignModal(true);
                                    }
                                  }
                                },
                                {
                                  headerName: 'Prev. Worked By',
                                  field: 'previouslyWorkedByName',
                                  minWidth: 160,
                                  filter: 'agSetColumnFilter',
                                  filterParams: { buttons: ['reset'] },
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  valueFormatter: (params) => {
                                    const prevWorkedBy = params.value;
                                    const assignedTo = params.data?.assignedToName;
                                    if (!prevWorkedBy || prevWorkedBy === assignedTo) return '\u2014';
                                    return prevWorkedBy;
                                  }
                                },
                                { headerName: 'Req. Date', field: 'requestCreatedDate', cellRenderer: RequestDateRenderer, minWidth: 150, maxWidth: 170, suppressMenu: false, floatingFilter: false, filter: 'agDateColumnFilter', filterParams: { buttons: ['reset'], suppressAndOrCondition: true, comparator: dateColumnComparator }, menuTabs: ['filterMenuTab'] },
                                {
                                  headerName: 'SLA',
                                  field: 'slaColor',
                                  minWidth: 70,
                                  maxWidth: 90,
                                  cellRenderer: (params) => {
                                    const hex = mapSlaColorToHex(params.value);
                                    return <AccessTime style={{ color: hex || '#bbbbbb', fontSize: '1.1rem' }} />;
                                  },
                                  filter: SlaColorFilter,
                                  filterParams: {},
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                },
                                {
                                  headerName: 'Status Date',
                                  field: 'requestStatusDate',
                                  minWidth: 150,
                                  suppressMenu: false,
                                  floatingFilter: false,
                                  menuTabs: ['filterMenuTab'],
                                  filter: 'agDateColumnFilter',
                                  filterParams: { buttons: ['reset'], comparator: dateColumnComparator },
                                  valueFormatter: (params) => {
                                    if (!params.value) return '\u2014';
                                    const date = new Date(params.value);
                                    const mm = String(date.getMonth() + 1).padStart(2, '0');
                                    const dd = String(date.getDate()).padStart(2, '0');
                                    const yyyy = date.getFullYear();
                                    const hh = String(date.getHours()).padStart(2, '0');
                                    const min = String(date.getMinutes()).padStart(2, '0');
                                    return `${mm}/${dd}/${yyyy} ${hh}:${min}`;
                                  }
                                },
                            ]}
                            defaultColDef={{ sortable: true, filter: true, floatingFilter: false, resizable: true }}
                            rowSelection={{ mode: 'multiRow', checkboxes: showBulkUI ? (params) => !params.data?.hasDecision : false, selectAll: 'filtered', enableClickSelection: false, headerCheckbox: showBulkUI && activeCard === 'unassignedCases' }}
                            onSelectionChanged={onSelectionChanged}
                            tooltipShowDelay={300}
                            tooltipHideDelay={0}
                            tooltipMouseTrack={true}
                            enableBrowserTooltips={false}
                            pagination={true}
                            paginationPageSize={20}
                            paginationPageSizeSelector={[20, 50, 100]}
                            getRowStyle={(params) => {
                              const status = params.data?.statusName || params.data?.internalStatus;
                              const isEscalated = status === REQUEST_STATUSES.ESCALATED || params.data?.isEscalated === true || (params.data?.requestFeedActivityTrackerDTOList || []).some(a => a?.isEscalated === true);
                              if (isEscalated) return { background: '#fff9c4' };
                              return null;
                            }}
                            onCellClicked={(params) => {
                              console.log('Cell clicked:', params);
                                const clickedColId = params.column?.getColId?.() || '';
                                const isSelectionColumn = clickedColId.includes('ag-Grid-SelectionColumn') || clickedColId.includes('ag-Grid-ControlsColumn');
                                const isAssignedToColumn = params.colDef?.field === 'assignedToName';
                                if (isSelectionColumn || isAssignedToColumn) return;
                                if (!params.data.projectId) return;
                                const LENDER_ADD_PROJECT_LENDER_REQUEST_STATUSES = [
                                  REQUEST_STATUSES.NEW,
                                  REQUEST_STATUSES.IN_PROGRESS,
                                  REQUEST_STATUSES.UNDER_REVIEW,
                                  REQUEST_STATUSES.ESCALATED,
                                  REQUEST_STATUSES.PENDING_REVIEW,
                                  REQUEST_STATUSES.ADDITIONAL_INFORMATION_RECEIVED,
                                  REQUEST_STATUSES.ADDITIONAL_INFORMATION_REQUIRED,
                                  REQUEST_STATUSES.NO_ACTION_TAKEN,
                                  REQUEST_STATUSES.UNRESPONSIVE,
                                ];
                                if (
                                  params.data.requestTypeName === REQUEST_TYPES.LENDER_ADD_PROJECT &&
                                  LENDER_ADD_PROJECT_LENDER_REQUEST_STATUSES.includes(params.data.statusName)
                                ) {
                                  window.location.href = '/lender-project-request/' + params.data.projectId;
                                } else {
                                  window.location.href = '/project/' + params.data.projectId;
                                } 
                            }}
                        />
                    </div>
                </div>
            </>
        ) : (
            <p>Loading dashboard data...</p>
        )}
        
        {/* Assignment Modal - Custom for LenderDashboard to prevent any refresh */}
        <AssignReviewerModal
            selectedRequest={selectedRequest}
            show={showAssignModal}
            setShow={setShowAssignModal}
            reviewers={reviewers}
            customHandler={handleAssignment}
            source="lender-dashboard"
        />
        {showBulkUI && (
          <AssignReviewerModal
            show={showBulkModal}
            setShow={(show) => { if (!show) { setIsBulkAssigning(false); } setShowBulkModal(show); }}
            reviewers={reviewers}
            isBulk={true}
            selectedRequests={selectedRows}
            onAssignComplete={handleBulkAssignComplete}
            onAssignError={handleBulkAssignError}
          />
        )}
    </div>
);
};

export default LenderDashboard;

